
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getAllListings, searchListings } from '../../services/listings';
import type { Listing } from '../../services/listings';
import { auth } from '../../lib/firebase';
import { useCreateConversation } from '../../hooks/useMessaging';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';
import { usePresence } from '../../hooks/usePresence';
import { useFavorites } from '../../hooks/useFavorites';
import { getUserProfile } from '../../services/auth';
import { openAIWidget } from '../../utils/aiWidget';

const SellerAvatarBadge: React.FC<{ sellerId?: string; sellerName?: string }> = ({ sellerId, sellerName }) => {
  const [photoURL, setPhotoURL] = useState<string | null>(null);
  const { presence } = usePresence(sellerId || null);
  const navigate = useNavigate();
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (sellerId) {
          const profile = await getUserProfile(sellerId);
          if (active) setPhotoURL(profile?.photoURL || null);
        }
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, [sellerId]);
  if (!sellerId) return null;
  const initial = (sellerName || 'S').charAt(0).toUpperCase();
  const online = Boolean(presence?.online);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); navigate(`/seller/${sellerId}`); }}
      className="absolute top-2 left-2 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 overflow-hidden flex items-center justify-center"
      aria-label="Open seller profile"
    >
      {photoURL ? (
        <img src={photoURL} alt={sellerName || 'Seller'} className="w-full h-full object-cover" />
      ) : (
        <span className="text-sm font-semibold text-gray-700">{initial}</span>
      )}
      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white ${online ? 'bg-green-500' : 'bg-gray-400'}`}></span>
    </button>
  );
};

// Inline seller info shown in the card content (right side)
const SellerInlineInfo: React.FC<{ sellerId?: string; sellerName?: string }> = ({ sellerId, sellerName }) => {
  const [photoURL, setPhotoURL] = useState<string | null>(null);
  const [storeName, setStoreName] = useState<string | null>(null);
  const { presence } = usePresence(sellerId || null);
  const navigate = useNavigate();
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (sellerId) {
          const profile = await getUserProfile(sellerId);
          if (active) { setPhotoURL(profile?.photoURL || null); setStoreName(profile?.username || null); }
        }
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, [sellerId]);
  if (!sellerId) return null;
  // Prefer the seller's store/vendor name (username) over their real name.
  const display = storeName || sellerName;
  const initial = (display || 'S').charAt(0).toUpperCase();
  const online = Boolean(presence?.online);
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={(e) => { e.stopPropagation(); navigate(`/seller/${sellerId}`); }}
        className="relative w-8 h-8 rounded-full bg-white shadow-sm border border-gray-200 overflow-hidden flex items-center justify-center"
        aria-label="Open seller profile"
      >
        {photoURL ? (
          <img src={photoURL} alt={display || 'Seller'} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs font-semibold text-gray-700">{initial}</span>
        )}
        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${online ? 'bg-green-500' : 'bg-gray-400'}`}></span>
      </button>
      {display && (
        <span className="text-xs text-gray-700 truncate max-w-[120px]">{display}</span>
      )}
    </div>
  );
};

// Deterministic "was" price + discount for display. Stable per listing id (no
// flicker on re-render) and varied 10–35% so items don't all read exactly -10%.
const computeDeal = (id: string | undefined, price: number): { pct: number; original: number } | null => {
  if (!price || price <= 0) return null;
  const s = id || String(price);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const pct = 10 + (h % 26); // 10–35% off
  const original = Math.round(price / (1 - pct / 100) / 50) * 50; // round "was" price to nearest ₦50
  if (original <= price) return null;
  return { pct, original };
};

// Short label for a long university name: the parenthetical acronym if present
// (e.g. "…(BOUESTI)"), else the part before the first comma, capped.
const shortUni = (name: string): string => {
  const m = name.match(/\(([^)]+)\)/);
  if (m) return m[1].trim();
  const first = name.split(',')[0].trim();
  return first.length > 24 ? first.slice(0, 24) + '…' : first;
};

const MarketplacePage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState<[string, string]>(['', '']);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUniversity, setSelectedUniversity] = useState('all');
  const [openGroup, setOpenGroup] = useState<{ title: string; items: Listing[] } | null>(null);
  const [isCreatingConversation, setIsCreatingConversation] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{lat: number; lng: number} | null>(null);
  const [locationPermissionAsked, setLocationPermissionAsked] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { createConversation } = useCreateConversation();
  const currentUser = auth.currentUser;
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();

  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  // sellerId -> university, joined from seller profiles (listings don't store it).
  const [sellerUni, setSellerUni] = useState<Record<string, string>>({});

  const isHousing = (l: Listing) => {
    const cat = (l.category || '').toLowerCase();
    return l.type === 'house' ||
      cat.includes('hostel') ||
      cat.includes('accommodation') ||
      cat.includes('apartment') ||
      cat.includes('housing') ||
      cat.includes('room') ||
      cat.includes('lodging');
  };

  const nonHousingListings = useMemo(() => listings.filter(l => !isHousing(l) && l.type !== 'ticket'), [listings]);

  // A listing's campus = its own university field if present, else its seller's.
  const uniOf = (l: Listing) => String((l as any).university || sellerUni[l.sellerId || ''] || '').trim();

  // Join seller universities for any sellers we haven't looked up yet.
  useEffect(() => {
    const ids = Array.from(new Set(nonHousingListings.map(l => l.sellerId).filter(Boolean) as string[]))
      .filter(id => !(id in sellerUni));
    if (ids.length === 0) return;
    let active = true;
    (async () => {
      const entries = await Promise.all(ids.map(async (id) => {
        try { const p = await getUserProfile(id); return [id, String((p as any)?.university || '').trim()] as const; }
        catch { return [id, ''] as const; }
      }));
      if (!active) return;
      setSellerUni(prev => {
        const next = { ...prev };
        entries.forEach(([id, uni]) => { next[id] = uni; });
        return next;
      });
    })();
    return () => { active = false; };
  }, [nonHousingListings]);

  const categories = useMemo(() => {
    const map: Record<string, { name: string; count: number }> = {};
    nonHousingListings.forEach(l => {
      const key = (l.category || 'Uncategorized').toLowerCase();
      const name = l.category || 'Uncategorized';
      map[key] = {
        name,
        count: (map[key]?.count || 0) + 1,
      };
    });

    const neatlyUsedCount = nonHousingListings.filter(l => (l.condition || '').toLowerCase() !== 'new').length;

    return [
      { id: 'all', name: 'All Items', count: nonHousingListings.length },
      { id: 'neatly-used', name: 'Neatly Used Item', count: neatlyUsedCount },
      ...Object.entries(map).map(([id, { name, count }]) => ({ id, name, count }))
    ];
  }, [nonHousingListings]);

  // Campuses that actually have goods READY FOR SALE (excludes sold-out), with counts.
  const universities = useMemo(() => {
    const map: Record<string, number> = {};
    nonHousingListings.forEach(l => {
      if (l.soldOut) return;
      const uni = uniOf(l);
      if (uni) map[uni] = (map[uni] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [nonHousingListings, sellerUni]);

  // Total goods ready for sale across all campuses (for the "All Campuses" tile).
  const readyForSaleCount = useMemo(
    () => nonHousingListings.filter(l => !l.soldOut).length,
    [nonHousingListings]
  );
  // Request location permission on entry
  useEffect(() => {
    if (locationPermissionAsked) return;
    
    const storedLocation = localStorage.getItem('userLocation');
    if (storedLocation) {
      try {
        const locationData = JSON.parse(storedLocation);
        // Check if location is not too old (24 hours)
        if (Date.now() - locationData.timestamp < 24 * 60 * 60 * 1000) {
          setUserLocation(locationData.coords);
          setLocationPermissionAsked(true);
          return;
        }
      } catch (e) {
        // Invalid stored location, continue to prompt
      }
    }
    
    setShowLocationPrompt(true);
    setLocationPermissionAsked(true);
  }, [locationPermissionAsked]);

  // Preselect category from query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = (params.get('category') || '').toLowerCase();
    if (categoryParam) setSelectedCategory(categoryParam);
  }, [location.search]);

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(location.search);
      const q = (params.get('q') || '').trim();
      setLoading(true);
      try {
        if (q) {
          const items = await searchListings(q.toLowerCase());
          setListings(items);
        } else {
          const items = await getAllListings();
          setListings(items);
        }
      } catch {
        setListings([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [location.search]);

  const filteredListings = nonHousingListings
    .filter(listing => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      const blob = `${listing.title || ''} ${listing.description || ''} ${listing.category || ''} ${listing.location || ''}`.toLowerCase();
      return blob.includes(q);
    })
    .filter(listing => {
      if (selectedUniversity === 'all') return true;
      return uniOf(listing) === selectedUniversity;
    })
    .filter(listing => {
      if (selectedCategory === 'all') return true;
      if (selectedCategory === 'neatly-used') return (listing.condition || '').toLowerCase() !== 'new';
      return ((listing.category || '').toLowerCase() === selectedCategory);
    })
    .filter(listing => {
      const priceNum = Number(listing.price);
      const price = Number.isNaN(priceNum) ? 0 : priceNum;
      const minStr = priceRange[0];
      const maxStr = priceRange[1];
      const min = minStr === '' ? -Infinity : Number(minStr);
      const max = maxStr === '' ? Infinity : Number(maxStr);
      return price >= min && price <= max;
    });

  const sortedListings = useMemo(() => {
    const arr = [...filteredListings];
    
    // Calculate distances for all listings if user location is available
    const listingsWithDistance = userLocation ? arr.map(listing => {
      let distanceKm = Infinity;
      
      // Try to extract coordinates from location string
      if (listing.location) {
        const coords = extractCoordinates(listing.location);
        if (coords) {
          distanceKm = calculateDistance(
            userLocation.lat, userLocation.lng,
            coords.lat, coords.lng
          );
        }
      }
      
      return {
        ...listing,
        distanceKm
      };
    }) : arr.map(listing => ({ ...listing, distanceKm: Infinity }));

    const byVerified = (a: Listing & { distanceKm?: number }, b: Listing & { distanceKm?: number }) => {
      const av = a.verified ? 1 : 0;
      const bv = b.verified ? 1 : 0;
      return bv - av; // verified first
    };
    
    const byNewest = (a: Listing, b: Listing) => {
      const as = (a as any).createdAt?.seconds || 0;
      const bs = (b as any).createdAt?.seconds || 0;
      return bs - as;
    };
    
    const byPriceLow = (a: Listing, b: Listing) => (a.price || 0) - (b.price || 0);
    const byPriceHigh = (a: Listing, b: Listing) => (b.price || 0) - (a.price || 0);
    
    const byDistance = (a: Listing & { distanceKm?: number }, b: Listing & { distanceKm?: number }) => {
      const aDist = a.distanceKm ?? Infinity;
      const bDist = b.distanceKm ?? Infinity;
      return aDist - bDist; // nearest first
    };
    
    const byRating = (a: Listing, b: Listing) => (b.rating || 0) - (a.rating || 0);

    const secondary = (a: Listing & { distanceKm?: number }, b: Listing & { distanceKm?: number }) => {
      switch (sortBy) {
        case 'price-low': return byPriceLow(a, b);
        case 'price-high': return byPriceHigh(a, b);
        case 'distance': return byDistance(a, b);
        case 'rating': return byRating(a, b);
        case 'newest':
        default:
          return byNewest(a, b);
      }
    };

    return listingsWithDistance.sort((a, b) => {
      const v = byVerified(a, b);
      if (v !== 0) return v;
      
      // If user has location, prioritize nearby items even when not sorting by distance
      if (userLocation && sortBy !== 'distance') {
        const aDist = a.distanceKm ?? Infinity;
        const bDist = b.distanceKm ?? Infinity;
        
        // If both items are within reasonable distance (50km), use secondary sort
        if (aDist <= 50 && bDist <= 50) {
          return secondary(a, b);
        }
        // If one is nearby and the other is far, prioritize nearby
        if (aDist <= 50 && bDist > 50) return -1;
        if (aDist > 50 && bDist <= 50) return 1;
      }
      
      return secondary(a, b);
    });
  }, [filteredListings, sortBy, userLocation]);

  // NEW: Pull out Hostel-related listings
  const hostelListings = useMemo(() => {
    return listings.filter(l => {
      const cat = (l.category || '').toLowerCase();
      return cat.includes('hostel') || cat.includes('accommodation');
    });
  }, [listings]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleLocationPermission = async (allow: boolean) => {
    setShowLocationPrompt(false);
    
    if (!allow) {
      // User denied location access
      return;
    }

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(coords);
          // Store location with timestamp
          localStorage.setItem('userLocation', JSON.stringify({
            coords,
            timestamp: Date.now()
          }));
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Continue without location
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }
  };

  // Calculate distance between two coordinates using Haversine formula
  function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // Distance in kilometers
    return distance;
  }

  // Extract coordinates from location string (format: "lat,lng" or address)
  function extractCoordinates(locationStr: string): {lat: number; lng: number} | null {
    const coordsMatch = locationStr.match(/^(-?\d+\.\d+),\s*(-?\d+\.\d+)$/);
    if (coordsMatch) {
      return {
        lat: parseFloat(coordsMatch[1]),
        lng: parseFloat(coordsMatch[2])
      };
    }
    return null;
  }

  const handleMessageSeller = async (listing: Listing) => {
    if (!currentUser) {
      // Redirect to login or show login modal
      alert('Please log in to message sellers');
      return;
    }

    if (!listing.sellerId) {
      alert('Seller information not available');
      return;
    }

    if (listing.sellerId === currentUser.uid) {
      alert('You cannot message yourself');
      return;
    }

    setIsCreatingConversation(listing.id ?? null);

    try {
      const conversationId = await createConversation(
        listing.sellerId,
        listing.seller ?? 'Unknown Seller',
        '',
        listing.id,
        listing.title
      );
      
      // Navigate to messages page with the conversation selected
      navigate(`/messages?conversation=${conversationId}`);
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Failed to start conversation. Please try again.');
    } finally {
      setIsCreatingConversation(null);
    }
  };

  // Collapse listings that share a groupId into a single "collection" card, preserving
  // sort order (a group appears where its first member falls). Lone members stay normal.
  // Collapse a seller's items into one grid card only while browsing — never while searching.
  const dedupeSellers = !searchQuery.trim();
  const displayCards = useMemo(() => {
    const membersByGroup: Record<string, Listing[]> = {};
    sortedListings.forEach((l) => {
      const gid = (l as any).groupId;
      if (gid) (membersByGroup[gid] ||= []).push(l);
    });
    const seen = new Set<string>();
    const seenSeller = new Set<string>();
    const cards: Array<{ kind: 'single'; listing: Listing } | { kind: 'group'; title: string; items: Listing[] }> = [];
    sortedListings.forEach((l) => {
      const gid = (l as any).groupId;
      if (gid) {
        if (seen.has(gid)) return;
        seen.add(gid);
        const items = membersByGroup[gid];
        if (items.length > 1) cards.push({ kind: 'group', title: (items[0] as any).groupTitle || 'Collection', items });
        else cards.push({ kind: 'single', listing: items[0] });
      } else {
        // While browsing, only the first item per seller shows on the grid; the rest live
        // behind the "+N more from this seller" viewer so one seller doesn't flood it.
        // When the user is SEARCHING, never dedupe — every matching product must show.
        const sid = l.sellerId;
        if (dedupeSellers && sid) {
          if (seenSeller.has(sid)) return;
          seenSeller.add(sid);
        }
        cards.push({ kind: 'single', listing: l });
      }
    });
    return cards;
  }, [sortedListings, dedupeSellers]);

  // All marketplace items keyed by seller, so a card can show "+N more from this seller".
  const sellerListings = useMemo(() => {
    const map: Record<string, Listing[]> = {};
    nonHousingListings.forEach((l) => {
      if (l.sellerId) (map[l.sellerId] ||= []).push(l);
    });
    return map;
  }, [nonHousingListings]);

  const renderCard = (listing: Listing) => {
    const media = listing.images?.[0] || listing.image;
    const sellerItems = listing.sellerId ? sellerListings[listing.sellerId] : undefined;
    const isVideo = !!media && /\.(mp4|webm|mov|m4v|mpeg|mpg|mkv)$/i.test(media);
    const fav = isFavorite(listing.id ?? '');
    const condition = (listing.condition || '').trim();
    // Price slashes removed per product decision — no synthetic discount badges.
    const deal: { pct: number; original: number } | null = null;
    return (
      <div
        key={listing.id}
        role="button"
        tabIndex={0}
        onClick={() => { if (!listing.soldOut) navigate(`/product/${listing.id}`); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !listing.soldOut) navigate(`/product/${listing.id}`); }}
        className={`group relative flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-teal-100 hover:shadow-lg ${listing.soldOut ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          {media ? (
            isVideo ? (
              <video src={media} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" muted playsInline />
            ) : (
              <img src={media} alt={listing.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            )
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-gray-300">
              <i className="ri-image-line text-3xl"></i>
            </div>
          )}
          <div className="absolute left-2 top-2 flex flex-col items-start gap-1">
            {listing.soldOut ? (
              <span className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-semibold text-white shadow">Sold Out</span>
            ) : (
              <>
                {deal && (
                  <span className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white shadow">-{deal.pct}% OFF</span>
                )}
                {condition && (
                  <span className="rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-medium capitalize text-gray-700 shadow-sm backdrop-blur-sm">{condition}</span>
                )}
              </>
            )}
          </div>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation();
              fav
                ? removeFromFavorites(listing.id ?? '')
                : addToFavorites({ id: listing.id ?? '', name: listing.title, price: formatPrice(listing.price), image: media || '' });
            }}
            aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
          >
            <i className={`${fav ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-600'} text-base`}></i>
          </button>
        </div>

        <div className="flex flex-1 flex-col p-3">
          <h3 className="line-clamp-1 text-sm font-medium text-gray-900">{listing.title}</h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-base font-bold text-teal-700">{formatPrice(listing.price)}</span>
            {deal && (
              <>
                <span className="text-xs text-gray-400 line-through">{formatPrice(deal.original)}</span>
                <span className="text-[11px] font-semibold text-green-600">{deal.pct}% off</span>
              </>
            )}
          </div>
          {listing.location && (
            <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
              <i className="ri-map-pin-line text-xs"></i>
              <span className="truncate">{listing.location}</span>
            </div>
          )}
          <div className="mt-2 border-t border-gray-100 pt-2">
            <SellerInlineInfo sellerId={listing.sellerId} sellerName={listing.seller} />
          </div>
          {sellerItems && sellerItems.length > 1 && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenGroup({ title: `${listing.seller || 'Seller'} · all items`, items: sellerItems }); }}
              className="mt-2 inline-flex items-center gap-1 self-start rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-700 transition-colors hover:bg-teal-100"
            >
              <i className="ri-stack-line"></i>+{sellerItems.length - 1} more from this seller
            </button>
          )}
        </div>
      </div>
    );
  };

  // A stacked "collection" card (YouTube-playlist style) that opens the group viewer.
  const renderGroupCard = (title: string, items: Listing[]) => {
    const cover = items[0];
    const media = cover.images?.[0] || cover.image;
    const prices = items.map(i => i.price || 0).filter(p => p > 0);
    const min = prices.length ? Math.min(...prices) : 0;
    return (
      <div
        key={`group-${(cover as any).groupId}`}
        role="button"
        tabIndex={0}
        onClick={() => setOpenGroup({ title, items })}
        onKeyDown={(e) => { if (e.key === 'Enter') setOpenGroup({ title, items }); }}
        className="group relative flex cursor-pointer flex-col"
      >
        {/* Stacked layers behind */}
        <div className="pointer-events-none absolute inset-x-2 -top-1.5 h-full rounded-xl bg-white shadow-sm ring-1 ring-gray-200/70"></div>
        <div className="pointer-events-none absolute inset-x-1 -top-0.5 h-full rounded-xl bg-white shadow-sm ring-1 ring-gray-200/80"></div>

        <div className="relative flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-lg">
          <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
            {media ? (
              <img src={media} alt={title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-gray-300"><i className="ri-stack-line text-3xl"></i></div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur-sm">
              <i className="ri-stack-fill"></i>{items.length}
            </span>
            <div className="absolute bottom-2 left-2 right-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-bold text-white shadow">
                <i className="ri-folder-2-fill"></i>Collection
              </span>
            </div>
          </div>
          <div className="flex flex-1 flex-col p-3">
            <h3 className="line-clamp-1 text-sm font-semibold text-gray-900">{title}</h3>
            <div className="mt-1.5 text-sm text-gray-700">
              <span className="font-bold text-teal-700">from {formatPrice(min)}</span>
              <span className="ml-1 text-xs text-gray-500">· {items.length} items</span>
            </div>
            <div className="mt-2 border-t border-gray-100 pt-2">
              <SellerInlineInfo sellerId={cover.sellerId} sellerName={cover.seller} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CampusHeader />
      
      {/* Location Permission Prompt */}
      {showLocationPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="text-center mb-4">
              <i className="ri-map-pin-line text-3xl text-blue-600 mb-3"></i>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Enable Location Access
              </h3>
              <p className="text-gray-600 text-sm">
                Allow location access to see items near you first and get the best local deals on campus.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleLocationPermission(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Not Now
              </button>
              <button
                onClick={() => handleLocationPermission(true)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Allow Location
              </button>
            </div>
          </div>
        </div>
      )}
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        {/* Hero Header */}
        <div className="relative mb-6 md:mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900 px-6 py-8 md:px-10 md:py-10 shadow-sm">
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-teal-500/20 blur-2xl"></div>
          <div className="pointer-events-none absolute -bottom-12 right-24 h-32 w-32 rounded-full bg-emerald-400/10 blur-2xl"></div>
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Campus Marketplace</h1>
              <p className="mt-2 text-sm md:text-base text-gray-300">Discover great deals from fellow students — verified sellers, escrow-protected.</p>
            </div>

            {/* Location Status */}
            <div className="flex items-center gap-3">
              {userLocation ? (
                <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-white ring-1 ring-white/15 backdrop-blur-sm">
                  <i className="ri-map-pin-2-line text-teal-300"></i>
                  <span className="text-sm">Showing nearby items</span>
                  <button
                    onClick={() => {
                      localStorage.removeItem('userLocation');
                      setUserLocation(null);
                      setShowLocationPrompt(true);
                    }}
                    className="text-gray-300 hover:text-white text-sm"
                    title="Refresh location"
                  >
                    <i className="ri-refresh-line"></i>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowLocationPrompt(true)}
                  className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-white ring-1 ring-white/15 backdrop-blur-sm transition-colors hover:bg-white/20"
                >
                  <i className="ri-map-pin-line text-teal-300"></i>
                  <span className="text-sm">Enable location</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Hostels section removed to keep marketplace item-only */}

        {/* New-user hint: tutorials live in the footer */}
        <Link
          to="/tutorials"
          className="mb-4 flex items-center gap-3 rounded-xl border border-teal-100 bg-teal-50 px-4 py-2.5 text-sm text-teal-800 transition-colors hover:bg-teal-100"
        >
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-600 text-white"><i className="ri-play-fill"></i></span>
          <span className="flex-1">New here? Watch short tutorials on how buying &amp; selling works — you can find them anytime in the <span className="font-semibold">footer</span>.</span>
          <i className="ri-arrow-right-line flex-shrink-0"></i>
        </Link>

        {/* Shop by Campus — university dropdown */}
        <div className="mb-6 md:mb-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg md:text-2xl font-bold text-gray-900">
              <i className="ri-school-fill text-teal-600"></i>
              Shop by Campus
            </h2>
            {selectedUniversity !== 'all' && (
              <button
                onClick={() => setSelectedUniversity('all')}
                className="inline-flex items-center gap-1 text-sm font-semibold text-teal-600 hover:text-teal-700"
              >
                <i className="ri-close-circle-line"></i>
                Clear
              </button>
            )}
          </div>

          {/* Dropdown — easier on mobile than a row of tiles */}
          <div className="relative md:max-w-md">
            <i className="ri-school-fill pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-teal-600"></i>
            <select
              value={selectedUniversity}
              onChange={(e) => setSelectedUniversity(e.target.value)}
              className="w-full appearance-none rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-10 text-sm font-semibold text-gray-900 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100"
            >
              <option value="all">All Campuses — {readyForSaleCount} {readyForSaleCount === 1 ? 'item' : 'items'}</option>
              {universities.map((u) => (
                <option key={u.name} value={u.name}>{u.name} ({u.count})</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          </div>
          {universities.length === 0 && (
            <p className="mt-2 text-sm text-gray-500">No campus-tagged items yet.</p>
          )}
        </div>

        <div className="grid lg:grid-cols-4 gap-4 md:gap-8">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-4 md:p-6 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-equalizer-line text-teal-600"></i>
                Filters
              </h3>

              {/* Categories */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">Categories</h4>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all">All Items</option>
                  {categories.filter(cat => cat.id !== 'all' && cat.id !== 'neatly-used').map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({category.count})
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setSelectedCategory('neatly-used')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer mt-2 ${
                    selectedCategory === 'neatly-used'
                      ? 'bg-teal-50 text-teal-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span>Neatly Used Item</span>
                    <span className="text-xs text-gray-400">{categories.find(cat => cat.id === 'neatly-used')?.count || 0}</span>
                  </div>
                </button>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">Price Range</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      placeholder="Min"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={priceRange[0]}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPriceRange([val, priceRange[1]]);
                      }}
                    />
                    <span className="text-gray-400">-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={priceRange[1]}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPriceRange([priceRange[0], val]);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Ask Campus Guru */}
              <div className="bg-teal-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <i className="ri-robot-fill text-teal-600"></i>
                  <span className="font-medium text-teal-900">Campus Guru</span>
                </div>
                <p className="text-sm text-teal-700 mb-3">
                  Need help finding something specific?
                </p>
                <button
                  onClick={() => {
                     openAIWidget('I need help finding a specific item');
                    }}
                  className="w-full bg-teal-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Ask Campus Guru
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Search bar */}
            <div className="relative mb-4">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-gray-400"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search items, categories, locations…"
                className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-10 text-sm text-gray-700 shadow-sm ring-1 ring-transparent transition focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>

            {/* Sort and View Options */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 rounded-xl bg-white p-3 shadow-sm ring-1 ring-gray-100">
              <div className="flex items-center gap-2 px-1">
                <span className="text-sm font-semibold text-gray-900">{loading ? 'Loading…' : filteredListings.length.toLocaleString()}</span>
                <span className="text-sm text-gray-500">{filteredListings.length === 1 ? 'item' : 'items'} found</span>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">Sort</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                >
                  <option value="newest">Newest First</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="distance">Nearest First</option>
                  <option value="rating">Highest Rated</option>
                </select>
              </div>
            </div>

            {/* Listings Grid */}
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                    <div className="aspect-[4/3] animate-pulse bg-gray-200" />
                    <div className="space-y-2 p-3">
                      <div className="h-3 w-3/4 animate-pulse rounded bg-gray-200" />
                      <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
                      <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredListings.length === 0 ? (
              <div className="text-center py-16 rounded-xl bg-white ring-1 ring-gray-100">
                <i className="ri-search-eye-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No product found</h3>
                <p className="text-gray-600 mb-4">Try one of these categories</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {categories
                    .filter(c => c.id !== 'all' && c.id !== 'neatly-used')
                    .slice(0, 6)
                    .map(c => (
                      <button key={c.id} onClick={() => setSelectedCategory(c.id)} className="px-3 py-1 border border-gray-200 rounded-full text-sm text-gray-700 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 transition-colors">
                        {c.name}
                      </button>
                    ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                {displayCards.map((card) =>
                  card.kind === 'group'
                    ? renderGroupCard(card.title, card.items)
                    : renderCard(card.listing)
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Group / collection viewer */}
      {openGroup && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onClick={() => setOpenGroup(null)}>
          <div
            className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl animate-[slideUp_0.25s_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-teal-600 to-emerald-600 p-4 text-white">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-white/80">
                  <i className="ri-folder-2-fill"></i> Collection
                </div>
                <h3 className="truncate text-lg font-bold">{openGroup.title}</h3>
                <p className="text-xs text-white/80">{openGroup.items.length} items · tap any item to view it fully</p>
              </div>
              <button onClick={() => setOpenGroup(null)} className="flex-shrink-0 rounded-full p-1 hover:bg-white/20" aria-label="Close">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="grid flex-1 grid-cols-2 gap-3 overflow-y-auto p-4 sm:grid-cols-3 md:grid-cols-4">
              {openGroup.items.map((it) => {
                const m = it.images?.[0] || it.image;
                const isVideo = !!m && /\.(mp4|webm|mov|m4v|mpeg|mpg|mkv)$/i.test(m);
                return (
                  <button
                    key={it.id}
                    onClick={() => { setOpenGroup(null); navigate(`/product/${it.id}`); }}
                    className="group flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="relative aspect-square overflow-hidden bg-gray-100">
                      {/* placeholder behind, shown if media is missing or fails to load */}
                      <div className="absolute inset-0 flex items-center justify-center text-gray-300"><i className="ri-image-line text-2xl"></i></div>
                      {m && (isVideo ? (
                        <video src={m} muted playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <img src={m} alt={it.title} loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ))}
                      {it.soldOut && <span className="absolute left-1.5 top-1.5 rounded-full bg-red-600 px-2 py-0.5 text-[9px] font-semibold text-white">Sold</span>}
                    </div>
                    <div className="p-2">
                      <p className="line-clamp-1 text-xs font-medium text-gray-900">{it.title}</p>
                      <p className="text-sm font-bold text-teal-700">{formatPrice(it.price)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MarketplacePage;